import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '@/lib/api';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Phone, PhoneIncoming, PhoneOutgoing, PhoneMissed, Clock, Users, BarChart3, TrendingUp, RefreshCw, Loader2, Calendar, Timer } from 'lucide-react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, Legend } from 'recharts';
import logger from '@/lib/logger';

const PERIODS = [
    { value: 'today', label: 'Today' },
    { value: 'yesterday', label: 'Yesterday' },
    { value: 'this_week', label: 'This Week' },
    { value: 'last_week', label: 'Last Week' },
    { value: 'this_month', label: 'This Month' },
    { value: 'last_month', label: 'Last Month' },
    { value: 'custom', label: 'Custom Range' },
];

function formatDuration(seconds) {
    if (!seconds) return '0m';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    if (h > 0) return `${h}h ${m}m`;
    if (m > 0) return `${m}m ${s}s`;
    return `${s}s`;
}

function StatCard({ title, value, subtitle, icon: Icon, color = 'text-primary' }) {
    return (
        <Card>
            <CardContent className="p-4">
                <div className="flex items-center justify-between">
                    <div>
                        <p className="text-xs text-muted-foreground uppercase tracking-wider">{title}</p>
                        <p className={`text-2xl font-bold mt-1 ${color}`}>{value}</p>
                        {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
                    </div>
                    <div className="p-2.5 rounded-xl bg-muted"><Icon className={`h-5 w-5 ${color}`} /></div>
                </div>
            </CardContent>
        </Card>
    );
}

export default function CallAnalyticsPage() {
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [monthlyTrend, setMonthlyTrend] = useState(null);
    const [loading, setLoading] = useState(true);
    const [period, setPeriod] = useState('today');
    const [customStart, setCustomStart] = useState('');
    const [customEnd, setCustomEnd] = useState('');
    const [teamFilter, setTeamFilter] = useState('all');
    const [agentFilter, setAgentFilter] = useState('all');
    const [teams, setTeams] = useState([]);
    const [agents, setAgents] = useState([]);
    const [sortBy, setSortBy] = useState('total_calls');
    const [sortDir, setSortDir] = useState('desc');

    const isAdmin = ['super_admin', 'admin', 'sales_manager'].includes(user?.role);
    // Strict TL — only sees own team's agents in the table + dropdowns.
    // SM gets the same shape as admin (all teams + team filter).
    const isStrictTL = user?.role === 'team_leader';

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams({ period });
            if (period === 'custom' && customStart && customEnd) {
                params.append('custom_start', customStart);
                params.append('custom_end', customEnd);
            }
            // Note: `teamFilter` is intentionally applied client-side (see `agentStats`
            // below) so toggling a team highlight is instant and doesn't require a refetch.
            if (agentFilter !== 'all') params.append('agent_id', agentFilter);

            const res = await api.get(`/3cx/analytics?${params.toString()}`);
            setData(res.data);
        } catch (err) {
            if (err.response?.status !== 403) toast.error('Failed to load call analytics');
            logger.error(err);
        } finally {
            setLoading(false);
        }
    }, [period, customStart, customEnd, agentFilter]);

    useEffect(() => { fetchData(); }, [fetchData]);

    // Separate fetch for the monthly trend charts — always the current month regardless
    // of the period selector, so the "throughout the month" trend stays meaningful even
    // when the user narrows the dashboard to "Today" or a custom window.
    useEffect(() => {
        (async () => {
            try {
                const res = await api.get('/3cx/analytics?period=this_month');
                setMonthlyTrend(res.data?.daily_distribution || []);
            } catch { /* ignore */ }
        })();
    }, []);

    useEffect(() => {
        const fetchTeams = async () => {
            try {
                const res = await api.get('/teams');
                let list = res.data || [];
                // Strict TLs only see their own team in the team filter
                // so they can't broaden the view to other teams.
                if (isStrictTL && user?.team_id) {
                    list = list.filter(t => t.id === user.team_id);
                }
                setTeams(list);
            } catch (e) { /* ignore */ }
        };
        const fetchAgents = async () => {
            try {
                const res = await api.get('/3cx/extensions');
                // Endpoint returns {mapped, unmapped, ...}. The legacy
                // `.filter(a => a.extension)` was a no-op on the object;
                // we don't strictly need this list anymore (`agentStats`
                // comes from `data.agents`) but keep the call so any
                // future change to the endpoint shape still hydrates.
                const list = Array.isArray(res.data) ? res.data : (res.data?.mapped || []);
                setAgents(list);
            } catch (e) { /* ignore */ }
        };
        fetchTeams();
        fetchAgents();
    }, [isStrictTL, user?.team_id]);

    const s = data?.summary || {};
    const agentStatsRaw = data?.agents || [];
    const hourly = data?.hourly_distribution || [];
    const teamBreakdown = data?.team_breakdown || [];

    // Client-side team filter on the agent table & agent-wise charts — instant, no re-fetch.
    // Fallback: if a row has no team_id we show it when no team selected; hide when a team
    // is selected. This matches HR's intent ("I clicked Team XLNC → only show their agents").
    const agentStats = useMemo(() => {
        let rows = agentStatsRaw;
        // Strict TL → always scope the table to their own team_id,
        // independent of whatever team filter they pick in the UI.
        if (isStrictTL && user?.team_id) {
            rows = rows.filter(a => a.team_id === user.team_id);
        }
        if (teamFilter === 'all') return rows;
        return rows.filter(a => a.team_id === teamFilter);
    }, [agentStatsRaw, teamFilter, isStrictTL, user?.team_id]);

    const sortedAgents = useMemo(() => {
        return [...agentStats].sort((a, b) => {
            const aVal = a[sortBy] || 0;
            const bVal = b[sortBy] || 0;
            return sortDir === 'desc' ? bVal - aVal : aVal - bVal;
        });
    }, [agentStats, sortBy, sortDir]);

    const handleSort = (col) => {
        if (sortBy === col) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
        else { setSortBy(col); setSortDir('desc'); }
    };

    const peakHour = useMemo(() => {
        if (!hourly.length) return null;
        return hourly.reduce((max, h) => h.calls > (max?.calls || 0) ? h : max, hourly[0]);
    }, [hourly]);

    if (loading && !data) {
        return <div className="flex items-center justify-center min-h-[400px]"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
    }

    return (
        <div className="space-y-6" data-testid="call-analytics-page">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold">3CX Call Analytics</h1>
                    <p className="text-muted-foreground">Team call performance & activity tracking</p>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                    <Select value={period} onValueChange={v => { setPeriod(v); if (v !== 'custom') { setCustomStart(''); setCustomEnd(''); } }}>
                        <SelectTrigger className="w-[150px]" data-testid="period-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            {PERIODS.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    {isAdmin && (
                        <Select value={teamFilter} onValueChange={v => { setTeamFilter(v); setAgentFilter('all'); }}>
                            <SelectTrigger className="w-[160px]" data-testid="team-filter"><SelectValue placeholder="All Teams" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Teams</SelectItem>
                                {teams.filter(t => t.active !== false).map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    )}
                    <Select value={agentFilter} onValueChange={setAgentFilter}>
                        <SelectTrigger className="w-[170px]" data-testid="agent-filter"><SelectValue placeholder="All Agents" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Agents</SelectItem>
                            {agents.map(a => <SelectItem key={a.user_id} value={a.user_id}>{a.name} ({a.extension})</SelectItem>)}
                        </SelectContent>
                    </Select>
                    <Button variant="outline" size="sm" onClick={fetchData} disabled={loading} data-testid="refresh-btn">
                        <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
                    </Button>
                </div>
            </div>

            {/* Custom Date Range */}
            {period === 'custom' && (
                <Card>
                    <CardContent className="p-4 flex flex-wrap items-end gap-4">
                        <div><Label className="text-xs text-muted-foreground">Start Date</Label><Input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)} className="w-[170px]" /></div>
                        <div><Label className="text-xs text-muted-foreground">End Date</Label><Input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)} className="w-[170px]" /></div>
                        <Button size="sm" onClick={fetchData} disabled={!customStart || !customEnd}><Calendar className="h-4 w-4 mr-1" /> Apply</Button>
                    </CardContent>
                </Card>
            )}

            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                <StatCard title="Total Calls" value={s.total_calls || 0} subtitle={`${s.answer_rate || 0}% answered`} icon={Phone} color="text-blue-500" />
                <StatCard title="Outbound" value={s.outbound || 0} subtitle="Dialed out" icon={PhoneOutgoing} color="text-green-500" />
                <StatCard title="Inbound" value={s.inbound || 0} subtitle="Received" icon={PhoneIncoming} color="text-purple-500" />
                <StatCard title="Missed" value={s.missed || 0} subtitle="Unanswered inbound" icon={PhoneMissed} color="text-red-500" />
                <StatCard title="Talk Time" value={`${s.total_duration_hrs || 0}h`} subtitle={`${s.total_duration_min || 0} minutes`} icon={Clock} color="text-amber-500" />
                <StatCard title="Active Agents" value={agentStats.length} subtitle={peakHour ? `Peak: ${peakHour.label}` : ''} icon={Users} color="text-cyan-500" />
            </div>

            {/* Hourly Distribution Chart */}
            <Card>
                <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2"><BarChart3 className="h-4 w-4" /> Call Volume by Hour</CardTitle>
                    <CardDescription>Hourly distribution of calls (UAE / GMT+4)</CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="h-[200px]">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={hourly} margin={{ top: 5, right: 10, left: -15, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                                <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={1} className="text-muted-foreground" />
                                <YAxis tick={{ fontSize: 10 }} className="text-muted-foreground" />
                                <Tooltip contentStyle={{ background: 'hsl(var(--popover))', border: '1px solid hsl(var(--border))', borderRadius: '8px', fontSize: '12px' }} />
                                <Bar dataKey="calls" radius={[3, 3, 0, 0]}>
                                    {hourly.map((entry, idx) => (
                                        <Cell key={idx} fill={entry.calls === peakHour?.calls ? 'hsl(var(--chart-1))' : 'hsl(var(--muted-foreground) / 0.3)'} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </CardContent>
            </Card>

            {/* Per-agent charts — Daily Talk Time & Unique Leads */}
            {agentStats.length > 0 && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <Card>
                        <CardHeader className="pb-2">
                            <CardTitle className="text-base flex items-center gap-2"><Clock className="h-4 w-4 text-amber-500" /> Talk Time — by Agent</CardTitle>
                            <CardDescription>Total minutes of talk time for each agent (top 20)</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="h-[320px]">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart
                                        data={sortedAgents.filter(a => a.total_duration_min > 0).slice(0, 20).map(a => ({
                                            name: a.agent_name.split(' ')[0],
                                            full_name: a.agent_name,
                                            talk_min: a.total_duration_min,
                                            calls: a.total_calls,
                                        }))}
                                        margin={{ top: 5, right: 15, left: -5, bottom: 40 }}
                                    >
                                        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                                        <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-35} textAnchor="end" className="text-muted-foreground" />
                                        <YAxis tick={{ fontSize: 10 }} className="text-muted-foreground"
                                            label={{ value: 'Minutes', angle: -90, position: 'insideLeft', style: { fontSize: 10, fill: 'hsl(var(--muted-foreground))' } }} />
                                        <Tooltip content={({ active, payload }) => {
                                            if (!active || !payload?.length) return null;
                                            const d = payload[0].payload;
                                            return (
                                                <div className="bg-popover border border-border rounded-lg shadow-lg p-2.5 text-xs">
                                                    <p className="font-semibold mb-1">{d.full_name}</p>
                                                    <p className="text-amber-500">Talk Time: {d.talk_min} min</p>
                                                    <p className="text-muted-foreground">{d.calls} calls</p>
                                                </div>
                                            );
                                        }} />
                                        <Bar dataKey="talk_min" name="Talk Time (min)" fill="hsl(var(--chart-1))" radius={[3, 3, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader className="pb-2">
                            <CardTitle className="text-base flex items-center gap-2"><Users className="h-4 w-4 text-blue-500" /> Unique Leads Called — by Agent</CardTitle>
                            <CardDescription>Distinct leads contacted by each agent (top 20)</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="h-[320px]">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart
                                        data={[...sortedAgents].sort((a, b) => (b.unique_leads || 0) - (a.unique_leads || 0))
                                            .filter(a => a.unique_leads > 0).slice(0, 20).map(a => ({
                                                name: a.agent_name.split(' ')[0],
                                                full_name: a.agent_name,
                                                leads: a.unique_leads || 0,
                                                calls: a.total_calls,
                                                talk_min: a.total_duration_min,
                                            }))}
                                        margin={{ top: 5, right: 15, left: -5, bottom: 40 }}
                                    >
                                        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                                        <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-35} textAnchor="end" className="text-muted-foreground" />
                                        <YAxis tick={{ fontSize: 10 }} className="text-muted-foreground"
                                            label={{ value: 'Leads', angle: -90, position: 'insideLeft', style: { fontSize: 10, fill: 'hsl(var(--muted-foreground))' } }} />
                                        <Tooltip content={({ active, payload }) => {
                                            if (!active || !payload?.length) return null;
                                            const d = payload[0].payload;
                                            return (
                                                <div className="bg-popover border border-border rounded-lg shadow-lg p-2.5 text-xs">
                                                    <p className="font-semibold mb-1">{d.full_name}</p>
                                                    <p className="text-blue-500">Unique Leads: {d.leads}</p>
                                                    <p className="text-muted-foreground">{d.calls} calls · {d.talk_min} min</p>
                                                </div>
                                            );
                                        }} />
                                        <Bar dataKey="leads" name="Unique Leads" fill="hsl(var(--chart-2))" radius={[3, 3, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            )}

            {/* Monthly trend charts — ALWAYS current month, regardless of period selector */}
            {(() => {
                const daily = monthlyTrend || [];
                if (!daily.length) return null;
                const fmtDate = (iso) => {
                    try {
                        const d = new Date(iso + 'T00:00:00Z');
                        return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
                    } catch { return iso; }
                };
                const chartData = daily.map(d => ({
                    ...d,
                    label: fmtDate(d.date),
                    talk_hrs: +(d.total_duration_min / 60).toFixed(2),
                }));
                return (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <Card>
                            <CardHeader className="pb-2">
                                <CardTitle className="text-base flex items-center gap-2"><TrendingUp className="h-4 w-4 text-amber-500" /> Talk Time — Trend</CardTitle>
                                <CardDescription>Total minutes per day + answered-calls overlay. A drop in the green line with a high amber bar signals lead-list quality issues.</CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className="h-[280px]">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <LineChart data={chartData} margin={{ top: 5, right: 15, left: -5, bottom: 30 }}>
                                            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                                            <XAxis dataKey="label" tick={{ fontSize: 10 }} angle={-35} textAnchor="end" className="text-muted-foreground" />
                                            <YAxis yAxisId="left" tick={{ fontSize: 10 }} className="text-muted-foreground"
                                                label={{ value: 'Minutes', angle: -90, position: 'insideLeft', style: { fontSize: 10, fill: 'hsl(var(--muted-foreground))' } }} />
                                            <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10 }} className="text-muted-foreground"
                                                label={{ value: 'Calls', angle: 90, position: 'insideRight', style: { fontSize: 10, fill: 'hsl(var(--muted-foreground))' } }} />
                                            <Tooltip content={({ active, payload }) => {
                                                if (!active || !payload?.length) return null;
                                                const d = payload[0].payload;
                                                return (
                                                    <div className="bg-popover border border-border rounded-lg shadow-lg p-2.5 text-xs">
                                                        <p className="font-semibold mb-1">{d.label}</p>
                                                        <p className="text-amber-500">Talk Time: {d.total_duration_min} min ({d.talk_hrs} hrs)</p>
                                                        <p className="text-emerald-500">Connected: {d.answered} / {d.total_calls}</p>
                                                        <p className="text-muted-foreground">Connection rate: {d.connection_rate}%</p>
                                                    </div>
                                                );
                                            }} />
                                            <Line yAxisId="left" type="monotone" dataKey="total_duration_min" name="Talk Time (min)" stroke="hsl(var(--chart-1))" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                                            <Line yAxisId="right" type="monotone" dataKey="answered" name="Calls Connected" stroke="hsl(142 70% 45%)" strokeWidth={2} strokeDasharray="4 3" dot={{ r: 2 }} activeDot={{ r: 4 }} />
                                            <Legend wrapperStyle={{ fontSize: 10, paddingTop: 4 }} />
                                        </LineChart>
                                    </ResponsiveContainer>
                                </div>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader className="pb-2">
                                <CardTitle className="text-base flex items-center gap-2"><TrendingUp className="h-4 w-4 text-blue-500" /> Unique Leads — Trend</CardTitle>
                                <CardDescription>Distinct leads called per day — reveals which days see the most activity</CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className="h-[280px]">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <LineChart data={chartData} margin={{ top: 5, right: 15, left: -10, bottom: 30 }}>
                                            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                                            <XAxis dataKey="label" tick={{ fontSize: 10 }} angle={-35} textAnchor="end" className="text-muted-foreground" />
                                            <YAxis tick={{ fontSize: 10 }} className="text-muted-foreground"
                                                label={{ value: 'Leads', angle: -90, position: 'insideLeft', style: { fontSize: 10, fill: 'hsl(var(--muted-foreground))' } }} />
                                            <Tooltip content={({ active, payload }) => {
                                                if (!active || !payload?.length) return null;
                                                const d = payload[0].payload;
                                                return (
                                                    <div className="bg-popover border border-border rounded-lg shadow-lg p-2.5 text-xs">
                                                        <p className="font-semibold mb-1">{d.label}</p>
                                                        <p className="text-blue-500">Unique Leads: {d.unique_leads}</p>
                                                        <p className="text-muted-foreground">{d.total_calls} calls</p>
                                                    </div>
                                                );
                                            }} />
                                            <Line type="monotone" dataKey="unique_leads" name="Unique Leads" stroke="hsl(var(--chart-2))" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                                        </LineChart>
                                    </ResponsiveContainer>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                );
            })()}

            {/* Team Breakdown + Agent Table side by side */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Team Breakdown */}
                {isAdmin && teamBreakdown.length > 0 && (
                    <Card className="lg:col-span-1">
                        <CardHeader className="pb-2 flex-row items-center justify-between">
                            <CardTitle className="text-base flex items-center gap-2"><Users className="h-4 w-4" /> Team Summary</CardTitle>
                            {teamFilter !== 'all' && (
                                <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setTeamFilter('all')} data-testid="clear-team-filter-btn">
                                    Clear filter
                                </Button>
                            )}
                        </CardHeader>
                        <CardContent>
                            <p className="text-[11px] text-muted-foreground mb-2">Click a team to filter the agent performance table.</p>
                            <div className="space-y-2">
                                {teamBreakdown.map(t => {
                                    const isActive = teamFilter === t.team_id;
                                    return (
                                        <div
                                            key={t.team_name}
                                            className={`p-3 rounded-lg border transition-all cursor-pointer ${
                                                isActive
                                                    ? 'bg-primary/10 border-primary/40 shadow-sm'
                                                    : 'bg-muted/50 border-transparent hover:bg-muted'
                                            }`}
                                            onClick={() => {
                                                if (!t.team_id) return;
                                                setTeamFilter(isActive ? 'all' : t.team_id);
                                            }}
                                            data-testid={`team-card-${t.team_name}`}
                                        >
                                            <div className="flex items-center justify-between">
                                                <span className={`font-medium text-sm ${isActive ? 'text-primary' : ''}`}>{t.team_name}</span>
                                                <Badge variant={isActive ? 'default' : 'outline'}>{t.agents} agents</Badge>
                                            </div>
                                            <div className="flex items-center gap-4 mt-1.5 text-xs text-muted-foreground">
                                                <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{t.total_calls}</span>
                                                <span className="flex items-center gap-1"><PhoneOutgoing className="h-3 w-3 text-green-500" />{t.outbound}</span>
                                                <span className="flex items-center gap-1"><PhoneIncoming className="h-3 w-3 text-purple-500" />{t.inbound}</span>
                                                <span className="flex items-center gap-1"><Timer className="h-3 w-3" />{t.total_duration_min}m</span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Agent Table */}
                <Card className={isAdmin && teamBreakdown.length > 0 ? 'lg:col-span-2' : 'lg:col-span-3'}>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-base flex items-center gap-2">
                            <TrendingUp className="h-4 w-4" /> Agent Performance
                            {teamFilter !== 'all' && (() => {
                                const t = teamBreakdown.find(tb => tb.team_id === teamFilter);
                                return t ? (
                                    <Badge variant="default" className="ml-2" data-testid="active-team-filter-badge">
                                        Filter: {t.team_name}
                                    </Badge>
                                ) : null;
                            })()}
                            <Badge variant="outline" className="ml-auto">{sortedAgents.length} agents</Badge>
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {sortedAgents.length === 0 ? (
                            <div className="text-center py-8 text-muted-foreground">
                                <Phone className="h-10 w-10 mx-auto mb-3 opacity-30" />
                                <p>No call data for this period</p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead className="w-8">#</TableHead>
                                            <TableHead>Agent</TableHead>
                                            <TableHead className="text-right cursor-pointer hover:text-primary" onClick={() => handleSort('total_calls')}>
                                                Calls {sortBy === 'total_calls' && (sortDir === 'desc' ? '↓' : '↑')}
                                            </TableHead>
                                            <TableHead className="text-right cursor-pointer hover:text-primary" onClick={() => handleSort('outbound')}>
                                                Out {sortBy === 'outbound' && (sortDir === 'desc' ? '↓' : '↑')}
                                            </TableHead>
                                            <TableHead className="text-right cursor-pointer hover:text-primary" onClick={() => handleSort('inbound')}>
                                                In {sortBy === 'inbound' && (sortDir === 'desc' ? '↓' : '↑')}
                                            </TableHead>
                                            <TableHead className="text-right cursor-pointer hover:text-primary" onClick={() => handleSort('missed')}>
                                                Missed {sortBy === 'missed' && (sortDir === 'desc' ? '↓' : '↑')}
                                            </TableHead>
                                            <TableHead className="text-right cursor-pointer hover:text-primary" onClick={() => handleSort('total_duration_sec')}>
                                                Talk Time {sortBy === 'total_duration_sec' && (sortDir === 'desc' ? '↓' : '↑')}
                                            </TableHead>
                                            <TableHead className="text-right cursor-pointer hover:text-primary" onClick={() => handleSort('unique_leads')}>
                                                Leads {sortBy === 'unique_leads' && (sortDir === 'desc' ? '↓' : '↑')}
                                            </TableHead>
                                            <TableHead className="text-right">Avg/Call</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {sortedAgents.map((a, idx) => (
                                            <TableRow key={a.extension} data-testid={`agent-row-${a.extension}`}>
                                                <TableCell className="text-muted-foreground font-mono text-xs">{idx + 1}</TableCell>
                                                <TableCell>
                                                    <div>
                                                        <span className="font-medium">{a.agent_name}</span>
                                                        <span className="text-xs text-muted-foreground ml-1.5">ext {a.extension}</span>
                                                    </div>
                                                </TableCell>
                                                <TableCell className="text-right font-bold">{a.total_calls}</TableCell>
                                                <TableCell className="text-right text-green-500">{a.outbound}</TableCell>
                                                <TableCell className="text-right text-purple-500">{a.inbound}</TableCell>
                                                <TableCell className="text-right">
                                                    {a.missed > 0 ? <span className="text-red-500 font-medium">{a.missed}</span> : <span className="text-muted-foreground">0</span>}
                                                </TableCell>
                                                <TableCell className="text-right font-medium">{formatDuration(a.total_duration_sec)}</TableCell>
                                                <TableCell className="text-right">
                                                    <span className="font-medium text-blue-500">{a.unique_leads || 0}</span>
                                                </TableCell>
                                                <TableCell className="text-right text-muted-foreground">{formatDuration(a.avg_duration_sec)}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
